import { ReactNode } from 'react';
import { TableColumn } from './types';
import { formatValue } from '../utils/dataUtils';

interface BaseTableProps<T> {
  data: T[];
  columns: TableColumn<T>[];
  onRowClick?: (item: T, index: number) => void;
  maxRows?: number;
  showRanking?: boolean;
  emptyMessage?: string;
  className?: string;
  keyExtractor?: (item: T, index: number) => string | number;
  rowClassName?: (item: T, index: number) => string;
}

export function BaseTable<T>({
  data,
  columns,
  onRowClick,
  maxRows,
  showRanking = false,
  emptyMessage = 'No data available',
  className = '',
  keyExtractor = (_, index) => index,
  rowClassName = () => '',
}: BaseTableProps<T>) {
  const displayData = maxRows ? data.slice(0, maxRows) : data;

  if (!displayData?.length) {
    return (
      <div className="flex items-center justify-center py-8 text-[var(--anl-text-muted)]">
        <p className="text-sm">{emptyMessage}</p>
      </div>
    );
  }

  const allColumns = showRanking
    ? [
        {
          key: 'rank',
          label: 'Rank',
          align: 'left' as const,
          className: '',
          render: (_: T, index: number) => (
            <div
              className="flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium"
              style={{ backgroundColor: 'var(--anl-metric-bg)', color: 'var(--anl-text)' }}
            >
              {index + 1}
            </div>
          ),
        },
        ...columns,
      ]
    : columns;

  return (
    <div className={`${className}`}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-[var(--anl-text)]">
          <thead>
            <tr className="border-b border-[var(--anl-border)]">
              {allColumns.map(column => (
                <th
                  key={column.key}
                  className={`px-2 py-3 font-medium text-[var(--anl-text)] ${
                    column.align === 'right'
                      ? 'text-right'
                      : column.align === 'center'
                        ? 'text-center'
                        : 'text-left'
                  } ${column.className || ''}`}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayData.map((item, index) => (
              <tr
                key={keyExtractor(item, index)}
                className={`border-b border-[var(--anl-border)] transition-colors ${
                  onRowClick ? 'cursor-pointer hover:bg-[var(--anl-surface-elev)]' : ''
                } ${rowClassName(item, index)}`}
                onClick={() => onRowClick?.(item, index)}
              >
                {allColumns.map(column => (
                  <td
                    key={column.key}
                    className={`px-2 py-3 ${
                      column.align === 'right'
                        ? 'text-right'
                        : column.align === 'center'
                          ? 'text-center'
                          : 'text-left'
                    } ${column.className || ''}`}
                  >
                    {column.render(item, index)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export const tableUtils = {
  textColumn: <T,>(
    key: string,
    label: string,
    accessor: (item: T) => string | number,
    options: { align?: 'left' | 'right' | 'center'; className?: string } = {},
  ): TableColumn<T> => ({
    key,
    label,
    render: item => <span className="text-[var(--anl-text)]">{accessor(item)}</span>,
    align: options.align || 'left',
    className: options.className,
  }),

  titleWithRankColumn: <T,>(
    key: string,
    label: string,
    titleAccessor: (item: T) => string,
    options: {
      maxLength?: number;
      showIcon?: boolean;
      iconRenderer?: (item: T, index: number) => ReactNode;
    } = {},
  ): TableColumn<T> => ({
    key,
    label,
    render: (item, index) => {
      const safeIndex = index ?? 0;
      const title = titleAccessor(item);
      const truncatedTitle =
        options.maxLength && title.length > options.maxLength
          ? title.substring(0, options.maxLength) + '...'
          : title;

      return (
        <div className="flex items-center space-x-2">
          {options.showIcon && (
            <div
              className="flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium"
              style={{ backgroundColor: 'var(--anl-metric-bg)', color: 'var(--anl-text)' }}
            >
              {safeIndex + 1}
            </div>
          )}
          <div>
            <div className="max-w-xs truncate font-medium text-[var(--anl-text)]" title={title}>
              {truncatedTitle}
            </div>
          </div>
          {options.iconRenderer?.(item, safeIndex)}
        </div>
      );
    },
    align: 'left',
  }),

  metricColumn: <T,>(
    key: string,
    label: string,
    accessor: (item: T) => number,
    options: {
      format?: 'number' | 'duration' | 'percentage';
      className?: string;
    } = {},
  ): TableColumn<T> => ({
    key,
    label,
    render: item => {
      const value = accessor(item);
      const formattedValue = formatValue(value, options.format);

      return (
        <span className={`font-medium text-[var(--anl-text)] ${options.className || ''}`}>
          {formattedValue}
        </span>
      );
    },
    align: 'right',
  }),

  changeColumn: <T,>(
    key: string,
    label: string,
    accessor: (item: T) => number | null,
    options: { unit?: string } = {},
  ): TableColumn<T> => ({
    key,
    label,
    render: item => {
      const change = accessor(item);

      if (change === null || change === undefined) {
        return <span className="text-[var(--anl-text-muted)]">—</span>;
      }

      const changeColor =
        change > 0
          ? 'var(--anl-success)'
          : change < 0
            ? 'var(--anl-error)'
            : 'var(--anl-text-muted)';
      const changeIcon = change > 0 ? '↗' : change < 0 ? '↘' : '—';

      return (
        <div className="flex items-center justify-end space-x-1">
          <span className="font-medium" style={{ color: changeColor }}>
            {change !== 0
              ? `${formatValue(change, 'percentage')}${options.unit && options.unit !== '%' ? options.unit : ''}`
              : '—'}
          </span>
          <span style={{ color: changeColor }}>{changeIcon}</span>
        </div>
      );
    },
    align: 'right',
  }),

  metricWithIconColumn: <T,>(
    key: string,
    label: string,
    accessor: (item: T) => number,
    icon: ReactNode,
    options: {
      format?: 'number' | 'duration' | 'percentage';
      iconColor?: string;
    } = {},
  ): TableColumn<T> => ({
    key,
    label,
    render: item => {
      const value = accessor(item);
      const formattedValue = formatValue(value, options.format);

      return (
        <div className="flex items-center justify-end space-x-1">
          <span className="font-medium text-[var(--anl-text)]">{formattedValue}</span>
          <span className={options.iconColor || 'text-[var(--anl-success)]'}>{icon}</span>
        </div>
      );
    },
    align: 'right',
  }),
};
